import { useMemo, useState } from "react"
import { IconDeviceFloppy, IconPencil, IconRefresh, IconRestore, IconX } from "@tabler/icons-react"
import { skipToken } from "@reduxjs/toolkit/query/react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { ScheduleToolbar } from "@/app/booking-calendar/components/ScheduleHeader"
import type { CalendarDate } from "@/app/booking-calendar/lib/siteTime"
import {
  type HostSlotPlanRequest,
  type SiteSlotPlanRequest,
  type SlotPlanTarget,
  useGetHostSlotPlanQuery,
  useGetHostWeekQuery,
  useGetSiteSlotPlanQuery,
  useGetSiteWeekQuery,
  useResetSlotPlanMutation,
  useSaveHostSlotPlanMutation,
  useSaveSiteSlotPlanMutation,
} from "../availabilityApi"
import {
  buildResetRequest,
  buildSaveRequest,
  cycleCell,
  describeDiff,
  describeSaveError,
  diffCells,
  editorStateFrom,
  type EditorState,
  isDirty,
  makeUnavailable,
  type SaveFailure,
  validate,
} from "../lib/editorModel"
import { type CellMarker, overrideKindLabel, overrideOverlay, overrideTarget } from "../lib/overrideOverlay"
import {
  editorMode,
  newOverrideEdit,
  OVERRIDE_EDIT_LABEL,
  type OverrideEditContext,
  type OverrideEditForm,
  type OverrideEditKind,
  overrideKindsFor,
  pendingCells,
  showOverrideButtons,
} from "../lib/overrideEditModel"
import { useSlotPlanJob } from "../lib/useSlotPlanJob"
import { isCurrentWeek, isPastWeek, mondayOf, stepWeek, weekRangeLabel, weekViewFrom } from "../lib/weekModel"
import { AvailabilityGrid } from "./AvailabilityGrid"
import { AvailabilityLegend } from "./AvailabilityLegend"
import { JobStatusPanel } from "./JobStatusPanel"
import { OverrideEditButtons, OverrideEditPanel } from "./OverrideEditPanel"
import { OverrideDetailDialog } from "../../components/OverrideDetailDialog"

function formatSetting(state: EditorState): { start: string; max: string } {
  return {
    start: state.activeFrom ? state.activeFrom.replace("T", " ") : "Already in effect",
    max: state.maxPerDay.trim() === "" ? "Team default" : state.maxPerDay,
  }
}

/** The dated week the page has chosen (AB#803, AmsAvailabilityWeekView on). */
export interface SlotPlanWeek {
  /** The Monday showing, "yyyy-MM-dd". */
  monday: CalendarDate
  /** Today on the Pacific clock: decides "This week" and which weeks are past (read-only). */
  today: CalendarDate
  /** What the stepper names: the site, or the host at the site. */
  title: string
  /** Show another week: the Monday to show. */
  onChangeWeek: (monday: CalendarDate) => void
}

/**
 * One schedule's grid (AB#781): read-only for appointments:view, with Edit for appointments:manage.
 * A save replaces the whole grid (PUT → 202 with a job id) and the job's progress shows here until it
 * lands; "Restore default" asks the server for the default grid, since the defaults are its to own.
 *
 * With `week` (AB#803) the view state is a dated week instead: the week endpoint's effective cells
 * under "Mon 10/19" headers, with a week stepper. "Edit default schedule" then edits the weekly
 * template exactly as before, with the week, its headers and the stepper put away until it closes;
 * the week itself is the page's (in the URL), so closing the editor returns to the same one.
 *
 * The week's cells an override changed carry its marker (AB#803); clicking one opens the override's
 * detail, shared with the Schedule Overrides page, where a manager can withdraw it. Withdrawing or
 * creating an override drops the week's cache, so the week is read again.
 *
 * The week also has a third state (AB#806): a manager's override buttons open an override edit — a
 * form for that kind of override, its window drawn as pending on the week, the server's dry run under
 * it — while the week and its stepper stay live. View ↔ edit default ↔ override edit: either edit
 * opens only from the view and closes back to it (lib/overrideEditModel). `hostEmail` is the host's
 * email for the host kinds, which the create request names them by.
 */
export function SlotPlanEditor({
  target,
  canManage,
  timeZone,
  week,
  hostEmail,
}: {
  target: SlotPlanTarget
  canManage: boolean
  timeZone: string
  week?: SlotPlanWeek
  /** For a host schedule, the email Cal knows the host by (AB#806); absent while unknown. */
  hostEmail?: string | null
}) {
  const siteQuery = useGetSiteSlotPlanQuery(target.kind === "site" ? target.teamSlug : skipToken)
  const hostQuery = useGetHostSlotPlanQuery(
    target.kind === "host" ? { teamSlug: target.teamSlug, calUserId: target.calUserId } : skipToken,
  )
  const query = target.kind === "site" ? siteQuery : hostQuery
  const { data, isLoading, isFetching, error, refetch } = query

  const loaded = useMemo(
    () => (data ? editorStateFrom(target.kind, data, timeZone) : null),
    [data, target.kind, timeZone],
  )

  const [draft, setDraft] = useState<EditorState | null>(null)
  // The override being made from the week (AB#806); null outside the override edit state.
  const [overrideEdit, setOverrideEdit] = useState<OverrideEditForm | null>(null)
  const overrideContext = useMemo<OverrideEditContext>(
    () => ({ target, hostEmail: hostEmail ?? null }),
    // The page rebuilds the target on every render; what it names is what matters.
    [target.kind, target.teamSlug, target.kind === "host" ? target.calUserId : null, hostEmail],
  )

  // The dated week: asked for only with `week`, so with the flag off nothing new is fetched, and not
  // while the default schedule is being edited, when it is put away. Tagged with the plan, so a save or
  // reset of this schedule drops it and it is read again, and with the overrides (AB#803).
  const wantWeek = !!week && draft === null
  const siteWeekQuery = useGetSiteWeekQuery(
    wantWeek && target.kind === "site" ? { teamSlug: target.teamSlug, date: week.monday } : skipToken,
  )
  const hostWeekQuery = useGetHostWeekQuery(
    wantWeek && target.kind === "host" ? { teamSlug: target.teamSlug, calUserId: target.calUserId, date: week.monday } : skipToken,
  )
  const weekQuery = target.kind === "site" ? siteWeekQuery : hostWeekQuery
  // currentData, not data: while another week loads, the last one's cells must not sit under its dates.
  const weekData = weekQuery.currentData
  const weekView = useMemo(
    () => (weekData ? weekViewFrom(target.kind, weekData) : null),
    [weekData, target.kind],
  )
  const overlay = useMemo(() => (weekView ? overrideOverlay(weekView) : null), [weekView])
  // The overrides of the marked cell last clicked, and which of them the detail shows.
  const [opened, setOpened] = useState<{ ids: string[]; current: string } | null>(null)

  const job = useSlotPlanJob(target)
  const [saveSite, { isLoading: savingSite }] = useSaveSiteSlotPlanMutation()
  const [saveHost, { isLoading: savingHost }] = useSaveHostSlotPlanMutation()
  const [reset, { isLoading: resetting }] = useResetSlotPlanMutation()
  const busy = savingSite || savingHost || resetting

  const [clientErrors, setClientErrors] = useState<string[]>([])
  const [failure, setFailure] = useState<SaveFailure | null>(null)
  const [confirmRestore, setConfirmRestore] = useState(false)

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error || !data) {
    const status = (error as { status?: number } | undefined)?.status
    return (
      <p className="text-sm text-destructive">
        {status === 404
          ? target.kind === "host"
            ? "That person does not host at this site."
            : "That site was not found."
          : "The schedule could not be loaded."}
      </p>
    )
  }

  if (!loaded) {
    return <p className="text-sm text-destructive">The server sent a grid window that cannot be drawn.</p>
  }

  const editing = draft !== null
  const shown = draft ?? loaded.state
  const diff = draft ? diffCells(loaded.state, draft) : null
  const dirty = draft ? isDirty(loaded.state, draft) : false
  const settings = formatSetting(shown)
  const pastWeek = week ? isPastWeek(week.monday, week.today) : false
  const showWeek = !!week && !editing
  const mode = editorMode(draft, overrideEdit)
  const overrideButtons = showOverrideButtons({ weekView: !!week, canManage, mode, jobRunning: job.isRunning, pastWeek })
  // The pending override's window over the visible week: drawn client-side from the form, so the
  // stepper shows it on whichever week it touches.
  const pending = overrideEdit && showWeek && weekView
    ? { cells: pendingCells(overrideEdit, weekView.columns, weekView.state.frame), label: OVERRIDE_EDIT_LABEL[overrideEdit.kind] }
    : undefined

  function openMarker(marker: CellMarker) {
    const ids = marker.overrides.map(o => o.id)
    setOpened({ ids, current: ids[0]! })
  }

  function refresh() {
    void refetch()
    if (week) void weekQuery.refetch()
  }

  function startOverrideEdit(kind: OverrideEditKind) {
    if (!week) return
    setOverrideEdit(newOverrideEdit(kind, week.monday, week.today))
    setClientErrors([])
    setFailure(null)
  }

  function startEditing() {
    setDraft(loaded!.state)
    setClientErrors([])
    setFailure(null)
  }

  function stopEditing() {
    setDraft(null)
    setClientErrors([])
  }

  async function submit() {
    if (!draft) return
    const errors = validate(draft, timeZone)
    setClientErrors(errors)
    setFailure(null)
    if (errors.length > 0) return

    const body = buildSaveRequest(draft, timeZone)
    try {
      const accepted = target.kind === "site"
        ? await saveSite({ teamSlug: target.teamSlug, body: body as SiteSlotPlanRequest }).unwrap()
        : await saveHost({ teamSlug: target.teamSlug, calUserId: target.calUserId, body: body as HostSlotPlanRequest }).unwrap()
      job.track(accepted.jobId)
      setDraft(null)
    } catch (err) {
      setFailure(describeSaveError(err))
    }
  }

  async function restoreDefault() {
    setConfirmRestore(false)
    const state = draft ?? loaded!.state
    const errors = validate(makeUnavailable(state), timeZone)
    setClientErrors(errors)
    setFailure(null)
    if (errors.length > 0) return

    try {
      const accepted = await reset({ target, action: "restore-default", body: buildResetRequest(state, timeZone) }).unwrap()
      job.track(accepted.jobId)
      setDraft(null)
    } catch (err) {
      setFailure(describeSaveError(err))
    }
  }

  async function reload() {
    setFailure(null)
    setDraft(null)
    await refetch()
  }

  return (
    <div className="flex flex-col gap-4">
      {job.jobId && <JobStatusPanel job={job.job} status={job.status} onDismiss={job.dismiss} />}

      {week && !editing && (
        <ScheduleToolbar
          unit="week"
          onNavDay={(delta) => week.onChangeWeek(stepWeek(week.monday, Math.sign(delta)))}
          onGoToday={() => week.onChangeWeek(mondayOf(week.today))}
          isToday={isCurrentWeek(week.monday, week.today)}
          todayLabel="This week"
          teamLabel={week.title}
          dayLabel={weekRangeLabel(week.monday)}
          slotMinutes={weekView?.state.frame.slotMinutes ?? loaded.state.frame.slotMinutes}
          summary={pastWeek ? "A past week: view only" : `${weekView?.state.frame.slotMinutes ?? loaded.state.frame.slotMinutes} min slots`}
        />
      )}

      <div className="flex flex-wrap items-end gap-4">
        {editing ? (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="availability-start">Start date-time (Pacific)</Label>
              <Input
                id="availability-start"
                type="datetime-local"
                className="w-56"
                value={draft.activeFrom}
                onChange={(e) => setDraft({ ...draft, activeFrom: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="availability-max">Max bookings per day</Label>
              <Input
                id="availability-max"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                className="w-32"
                placeholder="Team default"
                value={draft.maxPerDay}
                onChange={(e) => setDraft({ ...draft, maxPerDay: e.target.value })}
              />
            </div>
          </>
        ) : (
          <dl className="flex flex-wrap gap-6 text-sm">
            <div>
              <dt className="text-xs text-muted-foreground">Start date-time (Pacific)</dt>
              <dd>{settings.start}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Max bookings per day</dt>
              <dd>{settings.max}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Slot length</dt>
              <dd>{shown.frame.slotMinutes} minutes</dd>
            </div>
          </dl>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} disabled={isFetching || editing}>
            <IconRefresh className="size-4" />
            Refresh
          </Button>
          {overrideButtons && <OverrideEditButtons kinds={overrideKindsFor(target)} onStart={startOverrideEdit} />}
          {canManage && !editing && !pastWeek && !overrideEdit && (
            <Button size="sm" onClick={startEditing} disabled={job.isRunning}>
              <IconPencil className="size-4" />
              {week ? "Edit default schedule" : "Edit"}
            </Button>
          )}
          {canManage && editing && (
            <>
              <Button variant="outline" size="sm" onClick={() => setDraft(makeUnavailable(draft))} disabled={busy}>
                Make unavailable
              </Button>
              <Button variant="outline" size="sm" onClick={() => setConfirmRestore(true)} disabled={busy}>
                <IconRestore className="size-4" />
                Restore default
              </Button>
              <Button variant="ghost" size="sm" onClick={stopEditing} disabled={busy}>
                <IconX className="size-4" />
                Cancel
              </Button>
              <Button size="sm" onClick={() => void submit()} disabled={busy || !dirty}>
                <IconDeviceFloppy className="size-4" />
                Submit changes
              </Button>
            </>
          )}
        </div>
      </div>

      {editing && (
        <p className="text-xs text-muted-foreground">
          {target.kind === "site"
            ? "Click a cell to change it: unavailable → public → private → managed. Breaks cannot be changed."
            : "Click a cell to make the host available or unavailable then. Breaks cannot be changed."}
          {" "}A start date-time in the future leaves no availability until then; blank applies at once.
          {diff && describeDiff(diff) && <span className="font-medium"> Changes: {describeDiff(diff)}.</span>}
        </p>
      )}

      {overrideEdit && week && (
        <OverrideEditPanel
          form={overrideEdit}
          onChange={setOverrideEdit}
          context={overrideContext}
          monday={week.monday}
          today={week.today}
          onCancel={() => setOverrideEdit(null)}
          onCreated={() => setOverrideEdit(null)}
        />
      )}

      {!editing && job.isRunning && canManage && (
        <p className="text-xs text-muted-foreground">Editing opens again once the last save has been applied.</p>
      )}

      {loaded.droppedCells > 0 && (
        <p className="text-xs text-amber-700">
          {loaded.droppedCells} stored cell(s) fall outside this grid and are not shown; saving drops them.
        </p>
      )}

      {(clientErrors.length > 0 || failure) && (
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive" role="alert">
          <ul className="list-disc pl-5">
            {[...clientErrors, ...(failure?.messages ?? [])].map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
          {failure?.conflict && (
            <Button variant="outline" size="sm" className="mt-2" onClick={() => void reload()}>
              <IconRefresh className="size-4" />
              Reload the plan
            </Button>
          )}
        </div>
      )}

      {showWeek && weekView && weekView.droppedCells > 0 && (
        <p className="text-xs text-amber-700">
          {weekView.droppedCells} cell(s) of this week fall outside the grid and are not shown.
        </p>
      )}

      <AvailabilityLegend
        kind={target.kind}
        overrideKinds={showWeek && overlay ? overlay.legendKinds : undefined}
        pendingLabel={pending?.label}
      />

      {showWeek ? (
        weekQuery.isError ? (
          <p className="text-sm text-destructive">This week could not be loaded.</p>
        ) : !weekData ? (
          <Skeleton className="h-64 w-full" data-testid="week-loading" />
        ) : !weekView ? (
          <p className="text-sm text-destructive">The server sent a week that cannot be drawn.</p>
        ) : (
          // The effective cells, read-only: what CMS publishes for these dates. Never clicked to edit;
          // the default schedule is edited from "Edit default schedule".
          <AvailabilityGrid
            state={weekView.state}
            editable={false}
            columns={weekView.columns}
            onCycle={() => {}}
            markers={overlay?.markers ?? {}}
            onOpenMarker={openMarker}
            label={`Week of ${weekRangeLabel(weekView.monday)}`}
            pending={pending}
          />
        )
      ) : (
        <AvailabilityGrid
          state={shown}
          editable={editing && !busy}
          onCycle={(weekday, start) => draft && setDraft(cycleCell(draft, weekday, start))}
        />
      )}

      {week && (
        <OverrideDetailDialog
          id={opened?.current ?? null}
          onClose={() => setOpened(null)}
          canManage={canManage}
          withdrawable
          onWithdrawn={() => setOpened(null)}
          choices={opened?.ids.map(id => {
            const override = weekView?.overrides.find(o => o.id === id)
            return { id, label: override ? `${overrideKindLabel(override)}: ${overrideTarget(override)}` : "Override" }
          })}
          onChoose={(id) => setOpened(current => (current ? { ...current, current: id } : current))}
        />
      )}

      <AlertDialog open={confirmRestore} onOpenChange={setConfirmRestore}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore the default schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              {target.kind === "site"
                ? "This replaces the site's whole grid with the default public, private and managed hours "
                : "This replaces the host's availability with the default hours for this site "}
              and applies it straight away, with the start date-time and max per day above. Unsaved cell
              changes are discarded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void restoreDefault()}>Restore default</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
